/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react'
import _ from 'underscore'
import { Moment } from 'moment'
import { useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import BackToTopButton from 'components/BackToTopButton'

import NotFound from 'pages/NotFound'
import AlertsPanel from 'pages/Device/AlertsPanel'
import Status from 'pages/Device/Status'
import ThermostatRthPanel from 'pages/Device/ThermostatRthPanel'
import ConsumerRthPanel from 'pages/Device/ConsumerRthPanel'
import AwairRthPanel from 'pages/Device/AwairRthPanel'
import SystemConfigPanel from 'pages/Device/SystemConfigPanel'
import SystemComponentsPanel from 'pages/Device/SystemComponentsPanel'
import VerificationReportsPanel from 'pages/Device/VerificationReportsPanel'
import NotesPanel from 'pages/Device/NotesPanel'
import OptedOut from 'pages/Device/OptedOut'
import NotEnrolled from 'pages/Device/NotEnrolled'
import TopNavbar from 'pages/Device/TopNavbar'
import MaintenanceTrendsPanel from 'pages/Device/MaintenanceTrendsPanel'
import AssignedSystemTime from 'pages/Device/AssignedSystemTime'

import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import useDevice from 'hooks/useDevice'
import useScreenSize from 'hooks/useScreenSize'
import useSearchParams from 'hooks/useSearchParams'
import useTrackScrollPositionOnPage from 'hooks/useTrackScrollPositionOnPage'
import useTrackTimeOnPage from 'hooks/useTrackTimeOnPage'
import useHomeownerAlerts from 'hooks/useHomeownerAlerts'
import useVerificationReports from 'hooks/useVerificationReports'

import Brand from 'helpers/brand'
import GA4 from 'helpers/ga4'

import { NoData, LoadingSinglePanePage, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

type Props = {
  dealerId: number
  deviceId: string
  isActive: boolean
  isUnassignedDevice?: boolean
  refreshPageCallback?: () => void | null
  className?: string
}

const DeviceLegacy = ({ dealerId, deviceId, isActive, isUnassignedDevice = false, refreshPageCallback, className }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { reportCache } = useStoreContext() // TODO: no need to pass it down once support with backbone removed.
  const { session } = useSessionContext() // TODO: no need to pass it down once support with backbone removed.
  const awairRthDate = useSearchParams('awairRthDate')

  useHomeownerAlerts(deviceId)
  useVerificationReports(deviceId)

  const { device, isDeviceLoaded } = useDevice(dealerId, deviceId)
  const [thermostatRthDate, setThermostatRthDate] = useState(useSearchParams('thermostatRthDate'))
  const [bottomHeight, setBottomHeight] = useState(0)

  const rthRef = useRef<HTMLDivElement>(null)
  const brandName = Brand.brandName()

  const screenSize = useScreenSize()
  const { enabledFeatures } = useFeaturesContext()

  const addGA4EventsCallback = (labelText) => {
    const eventData = { category: 'Customer Detail Page', action: 'Click Device Details Nav Button', label: labelText }

    GA4.sendEvent(eventData)
  }

  useEffect(() => {
    const componentHeight = ((document.getElementById('notes')?.clientHeight) || (document.getElementById('system-components')?.clientHeight) || 0)
    const Height = componentHeight !== 0 ? document.body.clientHeight - componentHeight - 250 : 0

    setBottomHeight(!session.isFsr ? Height : (Height * 20 / 100) + Height)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceLoaded])

  let earlyReturnJsx
  if (!isDeviceLoaded) {
    earlyReturnJsx = <LoadingSinglePanePage /> // Loading
  } else if (!device) {
    earlyReturnJsx = <NotFound /> // device not found
  } else if (device.deviceModel?.includes('950')) { // not supported device
    earlyReturnJsx = <> <NoData><h1>NOT SUPPORTED</h1><h2>This device cannot be displayed because it's not supported.</h2></NoData> <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} /> </>
  } else if (!device.optedIn) {
    if (device.status === 'DEALER MISMATCH') {
      earlyReturnJsx = <> <NoData><h1>THIS DEVICE BELONGS TO ANOTHER DEALER</h1><h2>The dealer code entered on the device is different from your dealer code. Please contact {brandName} Support for assistance at 844-359-0270</h2></NoData> <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} /> </>
    } else if (device.status === 'OPTED OUT') {
      earlyReturnJsx = <> <OptedOut deviceType={device.deviceType} deviceModel={device.deviceModel} /> <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} /> </>
    } else { // not enrolled
      earlyReturnJsx = <> <NotEnrolled /> <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} /> </>
    }
  } else if (!device.capabilities.length) { // not data recived yet
    earlyReturnJsx = <> <NoData><h1>WAITING FOR DATA</h1><h2>NO DATA HAS BEEN RECEIVED FROM THE THERMOSTAT YET.</h2></NoData> <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} /> </>
  }

  useTrackTimeOnPage('device_details', [60, 90, 120, 300], !earlyReturnJsx)
  useTrackScrollPositionOnPage('device_details', [50, 75], !earlyReturnJsx)

  if (earlyReturnJsx) {
    return earlyReturnJsx
  }

  // Check if device exists to avoid bogus typescript "device null" errors.
  // If earlyReturnJsx is not null, there's an early return and device cannot be null because
  // one of the early return conditions is checking if device is null, but typescript can't figure that out.
  if (device) {
    const hasAlertsCapability = device.capabilities.includes('alarms')
    const hasCurrentStatusCapability = device.capabilities.includes('current_status')
    const hasRthCapability = device.capabilities.includes('runtime_history')
    const hasSysConfigCapability = device.capabilities.includes('sys_config')
    const hasCommunicatingComponentsCapability = device.capabilities.includes('communicating_components')
    const hasZoneStatus = device.zoningEnabled
    const hasMaintenanceTrends = device.hasMaintenanceTrends
    const showVerificationReportsPanel = device.deviceType === 'control_hub'

    const showDayRthCallback = (time: Moment) => {
      rthRef.current?.scrollIntoView() // navigates from alerts panel
      setThermostatRthDate(time.format('MM-DD-YYYY'))
      navigate({
        pathname: location.pathname,
        search: `?thermostatRthDate=${time.format('MM-DD-YYYY')}${!_.isEmpty(awairRthDate) ? '&awairRthDate=' + awairRthDate : ''}`
      })
    }

    return (
      <div className={className}>

        {screenSize !== 'small' &&
          <TopNavbar
            hasAlertsCapability={hasAlertsCapability}
            hasCurrentStatusCapability={hasCurrentStatusCapability}
            hasRthCapability={hasRthCapability}
            hasSysConfigCapability={hasSysConfigCapability}
            hasCommunicatingComponentsCapability={hasCommunicatingComponentsCapability}
            hasAwairDevice={device.hasAwairDevice}
            onLinkClickCallback={addGA4EventsCallback}
            hasConsumerRTH={device.consumerRth}
            hasMaintenanceTrends={enabledFeatures.includes('maintenance_trends') && hasMaintenanceTrends}
            displayNotesLink={!session.isFsr}
            hasZoneStatus={hasZoneStatus}
            showVerificationReportsLink={showVerificationReportsPanel}
          />
        }
        <S.DeviceWrapper>
          {hasAlertsCapability && (
          <AlertsPanel
            dealerId={dealerId}
            deviceId={deviceId}
            reportCache={reportCache}
            deviceTimeZone={device.timeZone}
            showDayRthCallback={showDayRthCallback}
          />
          )}

          {hasCurrentStatusCapability && (
          <Status
            deviceId={deviceId}
            hasAwairDevice={device.hasAwairDevice}
            reportCache={reportCache}
          />
          )}

          <BackToTopButton onClickCallback={addGA4EventsCallback} />

          {hasRthCapability && (
          <ThermostatRthPanel
            isActive={isActive}
            device={device}
            timeZone={device.timeZone}
            thermostatRthDate={thermostatRthDate}
            ref={rthRef}
            updateThermostatRthDateCallback={setThermostatRthDate}
          />
          )}

          {device.hasAwairDevice && (
          <AwairRthPanel
            deviceId={deviceId}
            timeZone={device.timeZone}
            datesWithData={device.awairDataPresence ?? []}
            awairDevices={device.awairDevices}
            awairRthDate={awairRthDate}
          />
          )}

          {device.consumerRth && (
          <ConsumerRthPanel device={device} />
          )}

          {enabledFeatures.includes('maintenance_trends') && device.hasMaintenanceTrends && (
          <MaintenanceTrendsPanel dealerId={dealerId} device={device} />
          )}

          {hasSysConfigCapability && (
          <SystemConfigPanel
            deviceId={deviceId}
            deviceType={device.deviceType}
            reportCache={reportCache}
            readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
          />
          )}

          {hasCommunicatingComponentsCapability && (
          <SystemComponentsPanel
            deviceId={deviceId}
            communicating={device.communicating}
            reportCache={reportCache}
            readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
          />
          )}

          {showVerificationReportsPanel && <VerificationReportsPanel />}

          {!session.isFsr && <NotesPanel device={device} refreshPageCallback={refreshPageCallback} />}

          <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} />
        </S.DeviceWrapper>
        {device && screenSize !== 'small' && (
          <S.EndPage addedHeight={bottomHeight} />
        )}

        <BackToTopButton onClickCallback={addGA4EventsCallback} />
      </div>
    )
  }
}

export default DeviceLegacy

const S = {
  EndPage: styled.div.withConfig({
    shouldForwardProp: (prop) => !['addedHeight'].includes(prop)
  }) <{ addedHeight: number }>`
    height: ${props => props.addedHeight}px;
  `,
  DeviceWrapper: styled.div`
    background-color: ${color('paneBackground')};
    padding-bottom: 20px;
    @media ${screenSize.small} {
      padding-bottom: 10px;
    }
  `
}
